import { useState } from "react";
import { UserRound } from "lucide-react";
import type { Player } from "@/lib/types";
import { cn } from "@/lib/cn";
import { usePlayerPhoto } from "./usePlayerPhoto";

function initialsFor(player: Player): string {
  return (
    player.display_name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "--"
  );
}

export function PlayerAvatar({
  player,
  size = "md",
  className,
}: {
  player: Player;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  // D15: keyed by the athlete when known, so every assignment of the
  // athlete shares (and refreshes) one photo.
  const url = usePlayerPhoto(
    "player",
    player.id,
    player.photo_filename,
    player.athlete_id,
  );
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const style =
    player.avatar_style === "female" || player.avatar_style === "male"
      ? player.avatar_style
      : "neutral";
  const jersey = player.jersey_number?.trim();
  const sizeClass = {
    sm: "h-9 w-9 text-xs",
    md: "h-11 w-11 text-sm",
    lg: "h-14 w-14 text-base",
  }[size];
  const badgeClass = {
    sm: "min-w-4 px-0.5 text-[0.55rem]",
    md: "min-w-5 px-1 text-[0.6rem]",
    lg: "min-w-6 px-1 text-[0.68rem]",
  }[size];

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded border border-rule bg-field font-display font-semibold text-ink",
        sizeClass,
        className,
      )}
      data-player-avatar="true"
      aria-hidden="true"
    >
      {url && url !== failedUrl ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
          onError={() => setFailedUrl(url)}
        />
      ) : (
        <>
          <span className="player-avatar-initials">{initialsFor(player)}</span>
          <span className={`player-avatar-art portrait-${style}`}>
            {style === "neutral" ? (
              <UserRound strokeWidth={1.5} />
            ) : (
              <>
                <span className="portrait-hair" />
                <span className="portrait-shirt" />
                <span className="portrait-neck" />
                <span className="portrait-face" />
                <span className="portrait-fringe" />
              </>
            )}
          </span>
        </>
      )}
      {jersey ? (
        <span
          data-avatar-jersey
          className={cn(
            "absolute bottom-0 right-0 inline-flex h-4 items-center justify-center rounded-tl border border-rule-strong bg-accent font-mono font-semibold leading-none text-accent-ink shadow-sm",
            badgeClass,
          )}
        >
          {jersey.slice(0, 3).toUpperCase()}
        </span>
      ) : null}
    </span>
  );
}
